import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['imageGrid', 'template', 'errors']
  static values = {
    maxImages: Number,
    uploadPath: String,
    jwt: String,
    discussionId: String,
    parentId: String,
    modelName: String,
    allowPaste:{ type: Boolean, default: false }
  }

  connect() {
    this.discussionIdValue = (this.discussionIdValue === null || this.discussionIdValue === '') ? null : this.discussionIdValue
    this.parentIdValue = (this.parentIdValue === null || this.parentIdValue === '') ? null : this.parentIdValue

    if (this.allowPasteValue) {
      this._boundHandlePaste = this.handlePaste.bind(this)
      document.addEventListener('paste', this._boundHandlePaste)
    }
  }

  disconnect() {
    if (this.allowPasteValue) {
      document.removeEventListener('paste', this._boundHandlePaste)
    }
  }

  handleMultipleFiles(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    const currentImages = this.imageGridTarget.querySelectorAll('.image-container').length
    const maxToUpload = Math.min(files.length, this.maxImagesValue - currentImages)

    // Upload each file
    for (let i = 0; i < maxToUpload; i++) {
      const file = files[i]
      this.createImageContainer(file)
    }

    if (currentImages + files.length > this.maxImagesValue) {
      this.errorsTarget.querySelector('.too-many-images').classList.remove('hidden')
    } else {
      this.errorsTarget.querySelector('.too-many-images').classList.add('hidden')
    }

    this._handleErrorsSpacing();

    event.target.value = ''
  }

  handlePaste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    const imageFiles = Array.from(items)
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean)

    if (imageFiles.length === 0) return

    const currentImages = this.imageGridTarget.querySelectorAll('.image-container').length
    const maxToUpload = Math.min(imageFiles.length, this.maxImagesValue - currentImages)

    for (let i = 0; i < maxToUpload; i++) {
      this.createImageContainer(imageFiles[i])
    }

    if (currentImages + imageFiles.length > this.maxImagesValue) {
      this.errorsTarget.querySelector('.too-many-images').classList.remove('hidden')
    } else {
      this.errorsTarget.querySelector('.too-many-images').classList.add('hidden')
    }

    this._handleErrorsSpacing()
  }

  _handleErrorsSpacing() {
    const hiddenDescendants = this.errorsTarget.querySelectorAll('.hidden');
    this.errorsTarget.style.marginBottom = hiddenDescendants.length === 2 ? "0px" : "4px";
  }

  createImageContainer(file) {
    // Create container from template
    const templateContent = this.templateTarget.content.cloneNode(true)
    const container = templateContent.querySelector('.image-wrapper')

    // Add class to track that this is an image container
    container.classList.add('image-container')

    // Add to the grid
    this.imageGridTarget.appendChild(container)

    // Start uploading the file
    this.uploadFileForContainer(file, container)

    if (this.imageGridTarget.querySelectorAll('.image-container').length > 0) {
      this.imageGridTarget.style.marginBottom = "4px";
    }

    return container
  }

  async uploadFileForContainer(file, container) {
    const preview = container.querySelector('.image-preview')
    const loading = container.querySelector('.loading')
    const error = container.querySelector('.error')
    const deleteButton = container.querySelector('button')

    // Show loading state
    loading.classList.remove('hidden')

    // Create form data and upload
    const formData = new FormData()
    formData.append('image', file)
    formData.append('jwt', this.jwtValue)
    // formData.append('discussion_id', this.discussionIdValue)
    formData.append(`${this.modelNameValue}_id`, this.parentIdValue)

    try {
      const response = await fetch(this.uploadPathValue, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content
        }
      })

      if (response.ok) {
        const data = await response.json()

        loading.classList.add('hidden')

        // Update with the server URL
        preview.style.backgroundImage = `url(${data.url})`;
        preview.classList.remove('hidden');

        // Set the image ID for deletion
        deleteButton.dataset.imageId = data.id

        // Add hidden input for form submission
        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.className = 'image-id-input'
        // hiddenInput.name = 'discussion_message[discussion_image_ids][]'
        hiddenInput.name = `${this.modelNameValue}_message[${this.modelNameValue}_image_ids][]`
        hiddenInput.value = data.id
        container.appendChild(hiddenInput)
      } else {
        loading.classList.add('hidden')
        error.classList.remove('hidden')

        if (response.status === 507) {
          this.errorsTarget.querySelector('.not-enough-space').classList.remove('hidden')
        }
      }
    } catch (error) {
      loading.classList.add('hidden')
      error.classList.remove('hidden')
    }
  }

  async removeImage(event) {
    event.preventDefault()
    const button = event.currentTarget
    const container = button.closest('.image-wrapper')
    const imageId = button.dataset.imageId

    if (imageId) {
      try {
        const response = await fetch(`${this.uploadPathValue}/${imageId}?jwt=${this.jwtValue}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector("[name='csrf-token']").content
          }
        })

        if (response.ok) {
          container.remove()
        }
      } catch (error) {
        console.error('Delete failed:', error)
      }
    } else {
      container.remove()
    }

    if (this.imageGridTarget.querySelectorAll('.image-container').length == 0) {
      this.imageGridTarget.style.marginBottom = "0px";
    }
  }
}